import { createBarChart, createChart } from '../../../../utils/services/chartApi';
import { LABEL_FORMATS, LABEL_TYPES, barChartTypes } from './chartConstants';

const randomNum = () => Math.floor(Math.random() * (235 - 52 + 1) + 52);
const randomRGB = () => [randomNum(), randomNum(), randomNum()];

export const getUniqueRgb = colors => {
  const rgb = randomRGB();
  if (colors.find(color => color.toString() === rgb.toString())) {
    return getUniqueRgb();
  }
  return rgb;
};

const getPieChartLabelAndType = (showLabels, formatValueType, chartStyle) => {
  let label = formatValueType;
  let labelType = chartStyle;
  if (showLabels) {
    if (
      !(
        chartStyle === LABEL_TYPES.WITH_DESCRIPTION ||
        chartStyle === LABEL_TYPES.OUTSIDE_WITH_STROKE
      )
    ) {
      label =
        formatValueType === LABEL_FORMATS.NUMBER
          ? LABEL_FORMATS.LABEL_AND_NUMBER
          : LABEL_FORMATS.LABEL_AND_PERCENTAGE;
    }
  } else if (!showLabels) {
    if (chartStyle === LABEL_TYPES.OUTSIDE_WITH_STROKE) {
      labelType = LABEL_TYPES.INSIDE;
    }
  }

  return { label, labelType };
};

const getPiePayload = (contents, isVariable) => {
  const {
    type,
    dataSource,
    labelFontSize,
    descriptionFontSize,
    valueFontSize,
    labelColor,
    descriptionColor,
    valueColor,
    showLegend,
    legendPosition,
    formatValueType,
    showLabels,
    chartStyle,
    fontFamily,
    baseVariable,
    labelVariable,
    valueVariable,
    descVariable,
    colorPalette,
    repeatVariable,
  } = contents;

  const sections = dataSource
    .filter(({ value }) => !!value)
    .map(item => {
      const { label, description, value, color } = item;
      const section = {
        name: label,
        description: description,
        quantity: Number(value),
        color: color,
      };
      return section;
    });

  const { label, labelType } = getPieChartLabelAndType(showLabels, formatValueType, chartStyle);

  const labelFontWeight = chartStyle === LABEL_TYPES.WITH_DESCRIPTION ? 'bold' : 'plain';

  let payload = {
    type: type,
    label: label,
    labelType: labelType,
    theme: 'theme1',
    width: 1200,
    height: 800,
    labelFont: {
      family: fontFamily,
      size: labelFontSize,
      color: labelColor,
      style: labelFontWeight,
    },
    descriptionFont: {
      family: fontFamily,
      size: descriptionFontSize,
      color: descriptionColor,
      style: 'plain',
    },
    valuesFont: { family: fontFamily, size: valueFontSize, color: valueColor, style: 'bold' },
    stroke: {
      weight: 2,
      color: labelColor,
    },
    backgroundColor: [0, 0, 0, 0],
    legendInfo: {
      show: showLegend,
      position: legendPosition,
      margin: 30,
      marginBetweenEntity: 5,
      shape: 'square',
      font: { family: fontFamily, size: labelFontSize, color: [0, 0, 0], style: 'normal' },
    },
    sections: sections,
  };
  if (isVariable) {
    const totalColorPalette = [...dataSource.map(({ color }) => color), ...colorPalette];
    payload = {
      ...payload,
      repeatVariable,
      baseVariable,
      labelVariable,
      valueVariable,
      descVariable,
      colorPalette: totalColorPalette,
    };
  }

  return payload;
};

const getBarPayload = (contents, isVariable) => {
  const {
    type,
    labels,
    seriesList,
    labelFontSize,
    valueFontSize,
    legendFontSize,
    valueInterval,
    labelColor,
    valueColor,
    legendColor,
    showLegend,
    showGrid,
    legendPosition,
    showLabels,
    chartStyle,
    fontFamily,
    baseVariable,
    labelVariable,
    seriesValueVariable,
    seriesNameVariable,
    colorPalette,
    repeatVariable,
    prefixValue,
    suffixValue,
    orientation,
  } = contents;

  let maxValue = 0;
  const sections = labels.map((label, index) => {
    return {
      name: label,
      bars: seriesList
        .map(seriesItem => {
          const { name, series, color } = seriesItem;
          if (series[index] === '') {
            return undefined;
          }
          const seriesValue = Number(series[index]);
          if (seriesValue > maxValue) {
            maxValue = seriesValue;
          }
          return {
            name: name,
            value: seriesValue,
            color: color,
          };
        })
        .filter(v => !!v),
    };
  });

  const intervalNum = Number(valueInterval);
  const maxRange = Math.ceil(maxValue / intervalNum) * intervalNum + intervalNum;

  let payload = {
    type: type,
    width: 800,
    height: 600,
    rangeStart: 0,
    rangeEnd: maxRange,
    rangeInterval: valueInterval,
    itemMargin: 0.05,
    numberFormat: 'compact', //compact,number,integer
    numberCurrencyFormat: 'none', //none,INR,USD,EURO etc.
    valueOnBarTop: 'none', // none,inside,outside,insideCircle
    highestValueBarColor: chartStyle === LABEL_TYPES.INFOGRAPHIC ? [100, 100, 100] : undefined,
    backgroundColor: [0, 0, 0, 0],
    barPeriods: sections,
    showGrid,
    showLabel: showLabels,
    chartStyle,
    prefixValue,
    suffixValue,
    orientation,
    legendInfo: {
      show: showLegend,
      position: legendPosition,
      font: { family: fontFamily, size: legendFontSize, color: legendColor, style: 'normal' },
    },
    labelFont: {
      family: fontFamily,
      size: labelFontSize,
      color: labelColor,
      style: 'normal',
    },
    valuesFont: {
      family: fontFamily,
      size: valueFontSize,
      color: valueColor,
      style: 'normal',
    },
  };
  if (isVariable) {
    const totalColorPalette = [...seriesList.map(({ color }) => color), ...colorPalette];
    payload = {
      ...payload,
      repeatVariable,
      baseVariable,
      labelVariable,
      seriesValueVariable,
      seriesNameVariable,
      colorPalette: totalColorPalette,
    };
  }

  return payload;
};

export const getChartPayload = (contents, isVariable) => {
  const { type } = contents;
  if (barChartTypes.includes(type)) {
    return getBarPayload(contents, isVariable);
  }
  return getPiePayload(contents, isVariable);
};

export const fetchChart = async payload => {
  if (barChartTypes.includes(payload.type)) {
    const data = await createBarChart(payload);
    return data;
  }
  const data = await createChart(payload);
  return data;
};
